import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import io, { Socket } from 'socket.io-client';
import { getApiUrl } from '../utils/api';

interface MarketData {
  symbol: string;
  price: number;
  volume24h: number;
  high24h: number;
  low24h: number;
  priceChange24h: number;
  priceChangePercent24h: number;
  marketCap: number;
}

interface Position {
  symbol: string;
  amount: number;
  averagePrice: number;
  currentPrice: number;
  pnl: number;
  pnlPercent: number;
  usdtValue: number;
}

interface TradingStats {
  daily: { profit: number; trades: number; winRate: number };
  weekly: { profit: number; trades: number; winRate: number };
  monthly: { profit: number; trades: number; winRate: number };
}

interface BotStatus {
  isRunning: boolean;
  nextExecution: number;
  lastExecuted: Date;
}

interface MarketSentiment {
  newsScore: number;
  socialScore: number;
  whaleActivity: string;
  fearGreedIndex: number;
  institutionalFlow: string;
}

interface OrderBook {
  bids: Array<{ price: number; quantity: number }>;
  asks: Array<{ price: number; quantity: number }>;
}

interface TradingDataState {
  btcMarketData: MarketData;
  position: Position;
  stats: TradingStats;
  botStatus: BotStatus;
  marketSentiment: MarketSentiment;
  orderBook: OrderBook | null;
  chartData: any[] | null;
  isConnected: boolean;
}

const initialState: TradingDataState = {
  btcMarketData: {
    symbol: 'BTCUSDT',
    price: 0,
    volume24h: 0,
    high24h: 0,
    low24h: 0,
    priceChange24h: 0,
    priceChangePercent24h: 0,
    marketCap: 0,
  },
  position: {
    symbol: 'BTC/USDT',
    amount: 0.25847,
    averagePrice: 43250.50,
    currentPrice: 0,
    pnl: 0,
    pnlPercent: 0,
    usdtValue: 0,
  },
  stats: {
    daily: { profit: 325.80, trades: 12, winRate: 75 },
    weekly: { profit: 1250.40, trades: 67, winRate: 72.5 },
    monthly: { profit: 4890.20, trades: 245, winRate: 73.8 },
  },
  botStatus: {
    isRunning: true,
    nextExecution: 900,
    lastExecuted: new Date(),
  },
  marketSentiment: {
    newsScore: 50,
    socialScore: 50,
    whaleActivity: 'MEDIUM',
    fearGreedIndex: 50,
    institutionalFlow: 'NEUTRAL',
  },
  orderBook: null,
  chartData: null,
  isConnected: false,
};

type TradingDataAction =
  | { type: 'SET_CONNECTION_STATUS'; payload: boolean }
  | { type: 'UPDATE_INITIAL_DATA'; payload: any }
  | { type: 'UPDATE_PRICE'; payload: any }
  | { type: 'UPDATE_BOT_STATUS'; payload: BotStatus }
  | { type: 'UPDATE_BOT_TIMER'; payload: BotStatus }
  | { type: 'UPDATE_ORDER_BOOK'; payload: OrderBook }
  | { type: 'UPDATE_CHART_DATA'; payload: any[] }
  | { type: 'UPDATE_MARKET_SENTIMENT'; payload: MarketSentiment }
  | { type: 'UPDATE_AI_ANALYSIS'; payload: any };

function tradingDataReducer(state: TradingDataState, action: TradingDataAction): TradingDataState {
  switch (action.type) {
    case 'SET_CONNECTION_STATUS':
      return { ...state, isConnected: action.payload };
    
    case 'UPDATE_INITIAL_DATA':
      return {
        ...state,
        position: action.payload.position || state.position,
        stats: action.payload.stats || state.stats,
        botStatus: action.payload.botStatus || state.botStatus,
        btcMarketData: action.payload.marketData || state.btcMarketData,
        chartData: action.payload.chartData || state.chartData,
      };
    
    case 'UPDATE_PRICE':
      return {
        ...state,
        btcMarketData: action.payload.marketData || state.btcMarketData,
        position: action.payload.position || state.position,
      };
    
    case 'UPDATE_BOT_STATUS':
      return {
        ...state,
        botStatus: action.payload,
      };
    
    case 'UPDATE_BOT_TIMER':
      return {
        ...state,
        botStatus: action.payload,
      };
    
    case 'UPDATE_ORDER_BOOK':
      return {
        ...state,
        orderBook: action.payload,
      };
    
    case 'UPDATE_CHART_DATA':
      return {
        ...state,
        chartData: action.payload,
      };
    
    case 'UPDATE_MARKET_SENTIMENT':
      return {
        ...state,
        marketSentiment: action.payload,
      };
    
    default:
      return state;
  }
}

interface TradingDataContextType {
  state: TradingDataState;
  socket: Socket | null;
  executeTradeAction: (action: string, amount: number) => void;
  startBot: () => void;
  stopBot: () => void;
}

const TradingDataContext = createContext<TradingDataContextType | undefined>(undefined);

interface TradingDataProviderProps {
  children: React.ReactNode;
}

export const TradingDataProvider: React.FC<TradingDataProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(tradingDataReducer, initialState);
  const [socket, setSocket] = React.useState<Socket | null>(null);

  useEffect(() => {
    // Socket.IO 연결 - 동적 URL 구성
    const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
    const host = window.location.hostname;
    const serverUrl = `${protocol}://${host}:4001`;
    
    console.log('🔗 TradingDataContext Socket 연결 시도:', serverUrl);
    
    const newSocket = io(serverUrl, {
      transports: ['websocket', 'polling'],
      timeout: 10000,
      forceNew: false
    });
    setSocket(newSocket);

    // 연결 상태 이벤트
    newSocket.on('connect', () => {
      console.log('✅ TradingDataContext Socket 연결됨');
      dispatch({ type: 'SET_CONNECTION_STATUS', payload: true });
    });

    newSocket.on('disconnect', () => {
      console.log('❌ TradingDataContext Socket 연결 끊김');
      dispatch({ type: 'SET_CONNECTION_STATUS', payload: false });
    });

    // 초기 데이터 수신
    newSocket.on('initialData', (data) => {
      console.log('📊 TradingDataContext 초기 데이터 수신:', data);
      try {
        dispatch({ type: 'UPDATE_INITIAL_DATA', payload: data });
      } catch (error) {
        console.error('❌ 초기 데이터 처리 오류:', error);
      }
    });

    // 실시간 가격 업데이트
    newSocket.on('priceUpdate', (data) => {
      try {
        console.log('💰 가격 업데이트 수신:', data);
        if (data && typeof data === 'object') {
          // marketData가 있는 경우
          if (data.marketData && typeof data.marketData === 'object') {
            const validMarketData = {
              symbol: data.marketData.symbol || 'BTCUSDT',
              price: Number(data.marketData.price) || 0,
              volume24h: Number(data.marketData.volume24h) || 0,
              high24h: Number(data.marketData.high24h) || 0,
              low24h: Number(data.marketData.low24h) || 0,
              priceChange24h: Number(data.marketData.priceChange24h) || 0,
              priceChangePercent24h: Number(data.marketData.priceChangePercent24h) || 0,
              marketCap: Number(data.marketData.marketCap) || 0
            };
            
            const validPosition = data.position ? {
              symbol: data.position.symbol || 'BTC/USDT',
              amount: Number(data.position.amount) || 0,
              averagePrice: Number(data.position.averagePrice) || 0,
              currentPrice: Number(data.position.currentPrice) || 0,
              pnl: Number(data.position.pnl) || 0,
              pnlPercent: Number(data.position.pnlPercent) || 0,
              usdtValue: Number(data.position.usdtValue) || 0
            } : undefined;
            
            dispatch({ 
              type: 'UPDATE_PRICE', 
              payload: { 
                marketData: validMarketData,
                position: validPosition
              } 
            });
          }
          // 직접 symbol이 있는 경우 (marketData 형태)
          else if (data.symbol) {
            const validMarketData = {
              symbol: data.symbol || 'BTCUSDT',
              price: Number(data.price) || 0,
              volume24h: Number(data.volume24h) || 0,
              high24h: Number(data.high24h) || 0,
              low24h: Number(data.low24h) || 0,
              priceChange24h: Number(data.priceChange24h) || 0,
              priceChangePercent24h: Number(data.priceChangePercent24h) || 0,
              marketCap: Number(data.marketCap) || 0
            };
            
            dispatch({ 
              type: 'UPDATE_PRICE', 
              payload: { marketData: validMarketData } 
            });
          }
        }
      } catch (error) {
        console.error('❌ 가격 업데이트 처리 오류:', error);
      }
    });

    // 봇 상태 업데이트
    newSocket.on('botStatusUpdate', (data) => {
      try {
        if (data) {
          dispatch({ type: 'UPDATE_BOT_STATUS', payload: data });
        }
      } catch (error) {
        console.error('❌ 봇 상태 업데이트 처리 오류:', error);
      }
    });

    newSocket.on('botTimerUpdate', (data) => {
      try {
        if (data) {
          console.log('⏰ 봇 타이머 업데이트 수신:', data);
          dispatch({ type: 'UPDATE_BOT_TIMER', payload: data });
        }
      } catch (error) {
        console.error('❌ 봇 타이머 업데이트 처리 오류:', error);
      }
    });

    // 오더북 업데이트
    newSocket.on('orderBookUpdate', (data) => {
      try {
        if (data) {
          dispatch({ type: 'UPDATE_ORDER_BOOK', payload: data });
        }
      } catch (error) {
        console.error('❌ 오더북 업데이트 처리 오류:', error);
      }
    });

    // 차트 데이터 업데이트
    newSocket.on('chartDataUpdate', (data) => {
      try {
        if (data && data.chartData) {
          dispatch({ type: 'UPDATE_CHART_DATA', payload: data.chartData });
        }
      } catch (error) {
        console.error('❌ 차트 데이터 업데이트 처리 오류:', error);
      }
    });

    // 시장 감정 업데이트
    newSocket.on('marketSentimentUpdate', (data) => {
      try {
        if (data) {
          dispatch({ type: 'UPDATE_MARKET_SENTIMENT', payload: data });
        }
      } catch (error) {
        console.error('❌ 시장 감정 업데이트 처리 오류:', error);
      }
    });

    // AI 분석 업데이트
    newSocket.on('aiAnalysisUpdate', (data) => {
      console.log('🤖 AI 분석 업데이트:', data);
    });

    // 거래 체결 알림
    newSocket.on('tradeExecuted', (data) => {
      console.log('💰 거래 체결:', data);
    });

    // 봇 실행 알림
    newSocket.on('botExecution', (data) => {
      console.log('🤖 봇 실행:', data);
    });

    // 연결 오류 처리
    newSocket.on('connect_error', (error) => {
      console.error('❌ TradingDataContext Socket 연결 오류:', error);
      dispatch({ type: 'SET_CONNECTION_STATUS', payload: false });
    });

    return () => {
      console.log('🔌 TradingDataContext Socket 연결 해제');
      newSocket.close();
    };
  }, []);

  const executeTradeAction = async (action: string, amount: number) => {
    try {
      const response = await fetch(`${getApiUrl()}/api/trade`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action, amount }),
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ 거래 실행 완료:', result);
      }
    } catch (error) {
      console.error('❌ 거래 실행 오류:', error);
    }
  };

  const startBot = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/api/bot/start`, {
        method: 'POST',
      });
      
      if (response.ok) {
        console.log('✅ 봇 시작');
      }
    } catch (error) {
      console.error('❌ 봇 시작 오류:', error);
    }
  };

  const stopBot = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/api/bot/stop`, {
        method: 'POST',
      });
      
      if (response.ok) {
        console.log('⏹️ 봇 정지');
      }
    } catch (error) {
      console.error('❌ 봇 정지 오류:', error);
    }
  };

  const contextValue: TradingDataContextType = {
    state,
    socket,
    executeTradeAction,
    startBot,
    stopBot,
  };

  return (
    <TradingDataContext.Provider value={contextValue}>
      {children}
    </TradingDataContext.Provider>
  );
};

export const useTradingData = (): TradingDataContextType => {
  const context = useContext(TradingDataContext);
  if (context === undefined) {
    throw new Error('useTradingData must be used within a TradingDataProvider');
  }
  return context;
}; 